import type { FC, ReactNode } from 'react'
import type { Locale } from '@/i18n/config'
import type { CaseStudyCard, ContentListItem, TopicRow } from '@/lib/content/types'

import { useMemo, useState } from 'react'
import { FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cmsKey } from '@/lib/portal/cms'
import { ActionLink } from '@/components/site/primitives'
import { ArticleCard } from '@/components/site/article-card'
import { field } from '@/components/site/article-meta'
import { useLocale } from '@/i18n/use-locale'
import { siteContent } from '@/i18n/site-content'

export interface ListingFilter {
    id: string
    label: string
    cms: string
}

/** Chips for the topics that the listed items actually use, in topic order. */
export const topicFilters = (items: Array<ContentListItem>, topics: Array<TopicRow>, locale: Locale): Array<ListingFilter> => {
    const used = new Set(items.map(item => item.topic_id).filter(Boolean))

    return topics
        .filter(topic => used.has(topic.id))
        .map(topic => {
            const name = field(topic, 'name', locale)
            return { id: topic.id, label: name.value, cms: cmsKey('topics', topic.id, name.column) }
        })
}

/** Chips for the industries of the listed case studies, tagged against the first study that names each one. */
export const industryFilters = (items: Array<CaseStudyCard>, locale: Locale): Array<ListingFilter> => {
    const filters = new Map<string, ListingFilter>()

    for (const item of items) {
        if (!item.industry || filters.has(item.industry)) continue
        const industry = field(item, 'industry', locale)
        filters.set(item.industry, { id: item.industry, label: industry.value, cms: cmsKey('case_studies', item.id, industry.column) })
    }

    return Array.from(filters.values())
}

interface ArticleListingProps {
    items: Array<ContentListItem>
    filters: Array<ListingFilter>
    filterOf: (item: ContentListItem) => string | null
    allLabel: string
    filterLabel: string
    emptyTitle: string
    emptyText: string
}

/**
 * A list page body: filter chips, the featured item (featured, else the newest)
 * as a large card and the rest in a grid. Filtering happens in the browser.
 */
export const ArticleListing: FC<ArticleListingProps> = ({ items, filters, filterOf, allLabel, filterLabel, emptyTitle, emptyText }): ReactNode => {
    const locale = useLocale()
    const copy = siteContent[locale].listing
    const [active, setActive] = useState<string | null>(null)

    const visible = useMemo(() => active ? items.filter(item => filterOf(item) === active) : items, [active, items, filterOf])
    const lead = visible.find(item => item.featured) ?? visible[0] ?? null
    const rest = lead ? visible.filter(item => item !== lead) : []

    const counts = useMemo(() => {
        const result = new Map<string, number>()

        for (const item of items) {
            const key = filterOf(item)
            if (key) result.set(key, (result.get(key) ?? 0) + 1)
        }

        return result
    }, [items, filterOf])

    if (items.length === 0) {
        return (
            <section className='bg-background py-16 sm:py-24'>
                <div className='container-page'>
                    <div className='relative overflow-hidden rounded-3xl border border-dashed border-border bg-mist px-6 py-20 text-center sm:px-12'>
                        <div className='bg-grid mask-radial pointer-events-none absolute inset-0 opacity-60' aria-hidden='true' />

                        <div className='relative'>
                            <span className='mx-auto flex size-12 items-center justify-center rounded-2xl border border-border bg-background text-primary'>
                                <FileText className='size-5' aria-hidden='true' />
                            </span>

                            <h2 className='mt-6 font-display text-3xl text-foreground sm:text-4xl'>
                                {emptyTitle}
                            </h2>

                            <p className='mx-auto mt-4 max-w-lg leading-relaxed text-muted-foreground'>
                                {emptyText}
                            </p>

                            <ActionLink href='/contact' variant='outline' arrow className='mt-8'>
                                {copy.emptyAction}
                            </ActionLink>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    const chipClass = (selected: boolean): string => cn(
        'inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        selected ? 'border-ink bg-ink text-white' : 'border-border bg-background text-foreground/75 hover:border-foreground/25 hover:text-foreground'
    )

    return (
        <section className='bg-background py-14 sm:py-20'>
            <div className='container-page'>
                {filters.length > 1 && (
                    <div role='group' aria-label={filterLabel} className='-mx-1 flex flex-wrap gap-2 px-1'>
                        <button type='button' className={chipClass(active === null)} aria-pressed={active === null} onClick={() => setActive(null)}>
                            {allLabel}
                            <span className='text-xs opacity-60'>{items.length}</span>
                        </button>

                        {filters.map(filter => (
                            <button
                                key={filter.id}
                                type='button'
                                className={chipClass(active === filter.id)}
                                aria-pressed={active === filter.id}
                                onClick={() => setActive(active === filter.id ? null : filter.id)}
                            >
                                <span data-mdlfy-cms={filter.cms}>{filter.label}</span>
                                <span className='text-xs opacity-60'>{counts.get(filter.id) ?? 0}</span>
                            </button>
                        ))}
                    </div>
                )}

                {lead ? (
                    <div className={cn(filters.length > 1 && 'mt-10')}>
                        <ArticleCard item={lead} featured as='h2' />
                    </div>
                ) : (
                    <p className='mt-10 text-muted-foreground'>
                        {copy.noMatches}
                    </p>
                )}

                {rest.length > 0 && (
                    <div className='mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                        {rest.map(item => (
                            <ArticleCard key={item.id} item={item} as='h2' />
                        ))}
                    </div>
                )}
            </div>
        </section>
    )
}
